import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Globe, Gauge } from "lucide-react";
import { GhostingPanel } from "@/components/GhostingPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, PageHeader, Spinner } from "@/components/Common";
import { Rich } from "@/components/Rich";
import { api, errMsg, isUpgrade } from "@/lib/api";

const useGet = (path) => {
  const [d, setD] = useState(undefined);
  useEffect(() => { api.get(path).then((r) => setD(r.data)).catch((e) => { setD(null); if (!isUpgrade(e)) toast.error(errMsg(e)); }); }, [path]);
  return [d, setD];
};

const Panel = ({ icon: Icon, title, data, testid, children }) => (
  <Card data-testid={testid}>
    <div className="mb-4 flex items-center gap-2"><Icon className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-lg text-zinc-100">{title}</h3></div>
    {children}
    {data === undefined ? <Spinner className="text-[#D4AF37]" /> : data === null ? <p className="text-sm text-zinc-500">Unavailable on your plan.</p> : <Rich value={data} />}
  </Card>
);

export default function Insights() {
  const [confidence] = useGet("/ai/confidence");
  const [ghosting] = useGet("/ghosting");
  const [calendar, setCalendar] = useGet("/calendar");
  const [goal, setGoal] = useState("");
  const [portfolio, setPortfolio] = useState(null);

  const saveGoal = async () => {
    try { await api.put("/calendar/goal", { weekly_goal: Number(goal) }); toast.success("Weekly goal saved"); setCalendar((await api.get("/calendar")).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const publish = async () => {
    try { setPortfolio((await api.post("/portfolio/publish")).data); toast.success("Portfolio published"); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { api.get("/portfolio").then((r) => r.data?.slug && r.data?.published !== false && setPortfolio(r.data)).catch(() => {}); }, []);
  const pSlug = portfolio && (portfolio.slug || (portfolio.url || "").split("?")[0].replace(/\/+$/, "").split("/").pop());
  const pUrl = pSlug ? `${window.location.origin}/u/${encodeURIComponent(pSlug)}` : null;
  const { url: _u, public_url: _pu, ...pInfo } = portfolio || {};

  return (
    <div>
      <PageHeader title="Insights" subtitle="Your confidence, ghosting risk, weekly cadence and public portfolio." />
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel icon={Gauge} title="Confidence" data={confidence} testid="confidence-panel" />
        <GhostingPanel data={ghosting} />
        <Panel icon={CalendarDays} title="Application calendar" data={calendar} testid="calendar-panel">
          <div className="mb-4 flex gap-2">
            <Input type="number" min={1} value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={`Weekly goal${calendar?.weekly_goal ? ` (now ${calendar.weekly_goal})` : ""}`} className="bg-[#09090B]" data-testid="weekly-goal-input" />
            <Button onClick={saveGoal} disabled={!goal} data-testid="weekly-goal-save">Set goal</Button>
          </div>
        </Panel>
        <Card data-testid="portfolio-panel">
          <div className="mb-4 flex items-center gap-2"><Globe className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-lg text-zinc-100">Public portfolio</h3></div>
          <p className="mb-4 text-sm text-zinc-500">Publish a shareable page built from your profile.</p>
          <Button onClick={publish} data-testid="portfolio-publish-button">Publish portfolio</Button>
          {portfolio && <div className="mt-4">{pUrl && <a href={pUrl} target="_blank" rel="noreferrer" className="break-all text-sm text-[#F3C649] underline" data-testid="portfolio-link">{pUrl}</a>}{pUrl && <Button size="sm" variant="outline" className="ml-3" onClick={() => navigator.clipboard?.writeText(pUrl).then(() => toast.success("Link copied"))} data-testid="portfolio-copy-link">Copy link</Button>}<div className="mt-3"><Rich value={pInfo} /></div></div>}
        </Card>
      </div>
    </div>
  );
}
